import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './News.css';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const newsPerPage = 6;

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news`);
      const data = await response.json();
      setNews(data.filter((item) => item.status === 'published'));
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(news.map((item) => item.category).filter(Boolean))];

  const filteredNews = news.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const indexOfLastNews = currentPage * newsPerPage;
  const indexOfFirstNews = indexOfLastNews - newsPerPage;
  const currentNews = filteredNews.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(filteredNews.length / newsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className='news-loading'>
        <div className='loading-spinner'></div>
        <p>Loading news...</p>
      </div>
    );
  }

  return (
    <div className='news'>
      {/* Hero Section */}
      <section className='news-hero'>
        <div className='news-hero-overlay'></div>
        <div className='news-hero-content'>
          <h1>Latest News</h1>
          <p>
            Stay informed with the latest updates and announcements from the world of international
            education
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className='news-main'>
        <div className='news-container'>
          {/* Search and Filter */}
          <div className='news-controls'>
            <div className='search-box'>
              <input
                type='text'
                placeholder='Search news...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='search-input'
              />
              <i className='search-icon'>🔍</i>
            </div>

            <div className='category-filter'>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className='category-select'
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className='news-results-info'>
            <p>
              Showing {currentNews.length} of {filteredNews.length} news
            </p>
          </div>

          {/* News Grid */}
          {currentNews.length > 0 ? (
            <div className='news-grid'>
              {currentNews.map((newsItem) => (
                <article key={newsItem.id} className='news-card'>
                  <div className='news-image'>
                    {newsItem.featured_image ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${newsItem.featured_image}`}
                        alt={newsItem.title}
                        onError={(e) => {
                          e.target.src = '/assets/img/placeholder-news.jpg';
                        }}
                      />
                    ) : (
                      <div className='news-image-placeholder'>
                        <i className='placeholder-icon'>📰</i>
                      </div>
                    )}
                    {newsItem.category && (
                      <span className='news-category'>{newsItem.category}</span>
                    )}
                  </div>

                  <div className='news-content'>
                    <div className='news-meta'>
                      <span className='news-date'>
                        <i className='date-icon'>📅</i>
                        {formatDate(newsItem.published_date || newsItem.created_at)}
                      </span>
                      <span className='news-author'>
                        <i className='author-icon'>👤</i>
                        {newsItem.author}
                      </span>
                    </div>

                    <h3 className='news-title'>
                      <Link to={`/news/${newsItem.id}`}>{newsItem.title}</Link>
                    </h3>

                    <p className='news-excerpt'>
                      {truncateText(
                        newsItem.summary || newsItem.content.replace(/<[^>]*>/g, ''),
                        150
                      )}
                    </p>

                    <div className='news-footer'>
                      <Link to={`/news/${newsItem.id}`} className='read-more-btn'>
                        Read More
                        <i className='arrow-icon'>→</i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className='no-news'>
              <div className='no-news-icon'>📰</div>
              <h3>No news found</h3>
              <p>Try adjusting your search criteria or check back later for new content.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='news-pagination'>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className='pagination-btn prev-btn'
              >
                <i className='prev-icon'>←</i>
                Previous
              </button>

              <div className='pagination-numbers'>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className='pagination-btn next-btn'
              >
                Next
                <i className='next-icon'>→</i>
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default News;
